import { Layout } from "@/components/layout/Layout";
import { ScrollFadeIn } from "@/components/ScrollFadeIn";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, DoorOpen, Ruler, Puzzle, Network } from "lucide-react";

const BG_IMAGE = "/cases/barcelona/hero.jpg";

/* ─── Persistent background layer ─────────────────────────────────── */
function PageBackground() {
  return (
    <div className="fixed inset-0 -z-10">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${BG_IMAGE}')` }}
      />
      <div className="absolute inset-0 bg-background/[0.82]" />
    </div>
  );
}

/* ─── Section 1 · Hero ──────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative flex min-h-[85vh] items-center justify-center px-6 py-32 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${BG_IMAGE}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/50 to-background/90" />
      <div className="relative z-10 mx-auto max-w-3xl text-center">
        <ScrollFadeIn>
          <h1 className="font-serif text-4xl leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
            Architectural Systems, resolved&nbsp;in&nbsp;detail.
          </h1>
        </ScrollFadeIn>
        <ScrollFadeIn delay={120}>
          <p className="mx-auto mt-8 max-w-2xl font-sans text-base leading-relaxed text-muted-foreground md:text-lg">
            Architecture is not defined only by form, but by how elements meet,
            align, and are constructed. MINZO supports designers in sourcing and
            coordinating architectural systems that hold precision — from
            drawings to built reality.
          </p>
        </ScrollFadeIn>
        <ScrollFadeIn delay={240}>
          <Button asChild size="lg" className="mt-10 uppercase tracking-widest text-xs font-sans">
            <Link to="/#concierge">
              Start an inquiry <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 2 · Intro ─────────────────────────────────────────────── */
function Intro() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mx-auto max-w-3xl">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              Beyond drawings. Into construction.
            </h2>
            <div className="mt-8 space-y-5 font-sans text-base leading-relaxed text-muted-foreground md:text-[17px]">
              <p>
                Architectural intent is often clear in drawings — precise edges,
                flush alignments, considered junctions. But during execution,
                systems get substituted, tolerances shift, and the built result
                loses the exactness that defined the design.
              </p>
              <p>
                MINZO helps designers bridge the gap between what's drawn and
                what's built — by sourcing and coordinating architectural systems
                that preserve the logic, proportion, and precision of the
                original design.
              </p>
            </div>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 3 · System Scope ───────────────────────────────────── */
const scopes = [
  {
    icon: DoorOpen,
    title: "Openings & Enclosures",
    body: "Doors, windows, and glazing systems — selected for profile minimalism, thermal performance, and how they define the threshold between interior and exterior.",
    detail: "The starting point of any architectural system. Every opening sets the proportional language that profiles, junctions, and custom elements must follow.",
  },
  {
    icon: Ruler,
    title: "Profiles & Sections",
    body: "Shadow gaps, skirting systems, ceiling trims, and transition profiles — the details that define how surfaces meet and how edges are resolved.",
    detail: "Profiles carry the visual DNA of openings into every surface junction. When aligned, the entire space reads as one continuous act of precision.",
  },
  {
    icon: Puzzle,
    title: "Custom Architectural Elements",
    body: "Bespoke staircases, partitions, screens, and integrated joinery — coordinated with fabrication partners when standard systems fall short.",
    detail: "Custom elements extend the system logic into unique conditions. Each piece is developed to share the same profile language and material discipline.",
  },
  {
    icon: Network,
    title: "System Coordination",
    body: "Ensuring alignment across hardware, sections, ceiling details, and partition logic — so every system reads as part of one architectural language.",
    detail: "Coordination is what binds the other three layers together. Without it, even well-specified systems drift apart during execution.",
  },
];

function SystemScope() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mb-4">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              System scope
            </h2>
            <p className="mt-4 max-w-2xl font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
              Four layers that depend on each other. Each layer inherits the logic of the one before it — from the proportions set by openings, through to the coordination that holds everything together on site.
            </p>
          </div>
        </ScrollFadeIn>

        <div className="mt-14 grid gap-5 sm:grid-cols-2">
          {scopes.map((s, i) => {
            const Icon = s.icon;
            return (
              <ScrollFadeIn key={s.title} delay={i * 120}>
                <div className="group relative rounded-lg border border-border/50 bg-background/50 backdrop-blur-sm p-7 transition-all duration-300 hover:border-primary/30 hover:bg-background/70">
                  {/* Step connector line */}
                  <div className="absolute top-7 right-7 font-sans text-[11px] font-medium uppercase tracking-[0.2em] text-primary/40">
                    {String(i + 1).padStart(2, "0")}
                  </div>

                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-md border border-border/60 bg-background/60 text-primary/70 transition-colors group-hover:border-primary/40 group-hover:text-primary">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>

                  <h3 className="font-serif text-lg text-foreground md:text-xl">{s.title}</h3>
                  <p className="mt-2.5 font-sans text-sm leading-relaxed text-muted-foreground">
                    {s.body}
                  </p>
                  <p className="mt-3 font-sans text-xs leading-relaxed text-muted-foreground/70 italic">
                    {s.detail}
                  </p>
                </div>
              </ScrollFadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─── Section 4 · Why Systems Matter ─────────────────────────────── */
function WhySystemsMatter() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mx-auto max-w-3xl">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              Why systems matter
            </h2>
            <div className="mt-8 space-y-5 font-sans text-base leading-relaxed text-muted-foreground md:text-[17px]">
              <p>
                Architecture lives in the details — in edges, joints, and
                alignments. The way a door frame meets the wall. The shadow gap
                that separates ceiling from partition. The tolerance between glass
                and structure. These are the moments that define whether a space
                feels precise or approximate.
              </p>
              <p>
                When architectural systems are resolved with care, the building
                reads as a single, coherent act. When they're substituted or
                loosely coordinated, even strong spatial concepts lose their
                clarity — and the gap between drawing and reality becomes visible.
              </p>
            </div>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 5 · Typical Problems ───────────────────────────────── */
const problems = [
  "Local systems don't match the profile language specified in the design.",
  "Substitutions during procurement alter the architectural expression.",
  "Joints and junctions lack the precision required by the detail drawings.",
  "Coordination between trades leads to misalignment at critical intersections.",
  "The gap between what's drawn and what's built widens during execution.",
  "Inconsistency across door, window, and partition systems fragments the spatial language.",
];

function TypicalProblems() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            Typical problems we help solve
          </h2>
        </ScrollFadeIn>
        <ul className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {problems.map((p, i) => (
            <ScrollFadeIn key={i} delay={i * 80}>
              <li className="flex items-start gap-3 font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/50" />
                {p}
              </li>
            </ScrollFadeIn>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─── Section 6 · How We Work ────────────────────────────────────── */
const steps = [
  { num: "01", title: "Understand architectural intent", body: "We begin with the design drawings, detail sections, and spatial logic — understanding what the systems need to achieve in terms of line, proportion, and performance." },
  { num: "02", title: "Define system logic", body: "Mapping the profile families, hardware standards, and junction details that need to remain consistent across the project — creating a system framework." },
  { num: "03", title: "Match and adapt", body: "Identifying systems that meet the design specification — or coordinating custom solutions when standard products don't hold the required precision." },
  { num: "04", title: "Coordinate execution", body: "Supporting procurement, trade alignment, and installation coordination — so what arrives on site matches the drawings and assembles as intended." },
];

function HowWeWork() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            How we work
          </h2>
        </ScrollFadeIn>
        <div className="mt-16 grid gap-12 sm:grid-cols-2">
          {steps.map((s, i) => (
            <ScrollFadeIn key={s.num} delay={i * 100}>
              <div>
                <span className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-primary/60">
                  {s.num}
                </span>
                <h3 className="mt-2 font-serif text-xl text-foreground">{s.title}</h3>
                <p className="mt-3 font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
                  {s.body}
                </p>
              </div>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section 7 · System Areas ───────────────────────────────────── */
const areas = [
  "Doors",
  "Windows",
  "Profiles",
  "Shadow gaps",
  "Staircases",
  "Partitions",
  "Joinery",
  "Façade",
];

function SystemAreas() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            System areas
          </h2>
        </ScrollFadeIn>
        <div className="mt-14 flex flex-wrap gap-3">
          {areas.map((a, i) => (
            <ScrollFadeIn key={a} delay={i * 50}>
              <span className="inline-block border border-border/60 bg-background/50 backdrop-blur-sm px-5 py-2.5 font-sans text-xs uppercase tracking-[0.12em] text-foreground/70">
                {a}
              </span>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section 8 · Brand Positioning ──────────────────────────────── */
function BrandPositioning() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mx-auto max-w-3xl">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              For designers who care about precision&nbsp;in&nbsp;detail
            </h2>
            <p className="mt-8 font-sans text-base leading-relaxed text-muted-foreground md:text-[17px]">
              Exactness over approximation. Logic over assumption. Coordination
              over improvisation. The right architectural systems don't draw
              attention — they disappear into the precision of the building
              itself. Every edge, every junction, every alignment should feel
              inevitable, quiet, and resolved.
            </p>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 9 · Final CTA ──────────────────────────────────────── */
function FinalCTA() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${BG_IMAGE}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/70 to-background/80" />
      <div className="relative mx-auto max-w-3xl text-center">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            Working on an architecture-led project?
          </h2>
        </ScrollFadeIn>
        <ScrollFadeIn delay={100}>
          <p className="mx-auto mt-6 max-w-xl font-sans text-base leading-relaxed text-muted-foreground">
            Share your drawings, detail sections, or system specifications. We
            can support sourcing, coordination, and execution alignment based
            on the needs of the project.
          </p>
        </ScrollFadeIn>
        <ScrollFadeIn delay={200}>
          <Button asChild size="lg" className="mt-10 uppercase tracking-widest text-xs font-sans">
            <Link to="/#concierge">
              Start an architectural inquiry <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Page ────────────────────────────────────────────────────────── */
export default function ArchitecturalSystems() {
  return (
    <Layout>
      <PageBackground />
      <Hero />
      <Intro />
      <SystemScope />
      <WhySystemsMatter />
      <TypicalProblems />
      <HowWeWork />
      <SystemAreas />
      <BrandPositioning />
      <FinalCTA />
    </Layout>
  );
}
